/**
 * Secure Error Handler
 *
 * Implements secure error handling that prevents sensitive information
 * from being exposed to the client while maintaining proper logging.
 */

import { log } from "../config/env";
import { apiErrorCodeFromUnknown } from "./apiErrorUtils";
import { messageForClaimPendingEditRestriction } from "./claimPendingEditErrors";

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  requestId?: string;
  timestamp?: number;
}

export interface SanitizedError {
  message: string;
  code?: string;
  context?: string;
  timestamp: number;
  requestId?: string;
}

export class SecureErrorHandler {
  private static instance: SecureErrorHandler | null = null;
  private errorPatterns: RegExp[] = [];
  private sensitivePatterns: RegExp[] = [];

  constructor() {
    this.initializePatterns();
  }

  static getInstance(): SecureErrorHandler {
    if (!SecureErrorHandler.instance) {
      SecureErrorHandler.instance = new SecureErrorHandler();
    }
    return SecureErrorHandler.instance;
  }

  private initializePatterns(): void {
    // Patterns that indicate sensitive information
    this.sensitivePatterns = [
      /password/i,
      /token/i,
      /secret/i,
      /key/i,
      /auth/i,
      /credential/i,
      /bearer/i,
      /jwt/i,
      /session/i,
      /cookie/i,
      /ssn/i,
      /social_security/i,
      /credit_card/i,
      /bank_account/i,
      /private_key/i,
      /api_key/i,
    ];

    // Patterns that indicate system information
    this.errorPatterns = [
      /database/i,
      /sql/i,
      /connection/i,
      /server/i,
      /internal/i,
      /stack trace/i,
      /file path/i,
      /line number/i,
      /column number/i,
      /psycopg2/i,
      /sqlalchemy/i,
      /undefinedcolumn/i,
      /programmingerror/i,
      /\[sql:/i,
      /background on this error/i,
    ];
  }

  /**
   * True when a server message looks like a raw DB / SQL dump.
   */
  private looksLikeInternalDbError(message: string): boolean {
    if (!message || typeof message !== "string") return false;
    const lower = message.toLowerCase();
    return (
      lower.includes("psycopg2") ||
      lower.includes("sqlalchemy") ||
      lower.includes("undefinedcolumn") ||
      lower.includes("[sql:") ||
      lower.includes("data error: (") ||
      /column\s+\w+\.\w+\s+does not exist/i.test(message) ||
      lower.includes("background on this error")
    );
  }

  private userFacingDbFallback(): string {
    return "We could not load team data. Please refresh and try again.";
  }

  /**
   * Sanitize error message for client consumption
   */
  sanitizeErrorMessage(error: unknown, context: string = "Unknown"): string {
    let errorMessage = "An unexpected error occurred";

    if (error instanceof Error) {
      errorMessage = this.sanitizeString(error.message);
    } else if (typeof error === "string") {
      errorMessage = this.sanitizeString(error);
    } else if (error && typeof error === "object") {
      // Handle error objects with message property
      const errorObj = error as {
        message?: string;
        detail?: string;
        error?: string;
      };
      if (errorObj.message) {
        errorMessage = this.sanitizeString(errorObj.message);
      } else if (errorObj.detail) {
        errorMessage = this.sanitizeString(errorObj.detail);
      } else if (errorObj.error) {
        errorMessage = this.sanitizeString(errorObj.error);
      }
    }

    // Log the full error server-side for debugging
    this.logError(error, context);

    return errorMessage;
  }

  /**
   * Sanitize a string to remove sensitive information
   */
  private sanitizeString(str: string): string {
    if (!str || typeof str !== "string") {
      return "An unexpected error occurred";
    }

    let sanitized = str;

    // Remove sensitive patterns
    this.sensitivePatterns.forEach((pattern) => {
      sanitized = sanitized.replace(pattern, "[REDACTED]");
    });

    // Remove system information patterns
    this.errorPatterns.forEach((pattern) => {
      sanitized = sanitized.replace(pattern, "[SYSTEM]");
    });

    // Remove file paths and line numbers
    sanitized = sanitized.replace(/\/[^\s]+\.[a-z]+:\d+:\d+/gi, "[FILE_PATH]");
    sanitized = sanitized.replace(/at\s+[^\s]+\s+\([^)]+\)/gi, "[STACK_TRACE]");

    // Remove URLs and IP addresses
    sanitized = sanitized.replace(/https?:\/\/[^\s]+/gi, "[URL]");
    sanitized = sanitized.replace(
      /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g,
      "[IP_ADDRESS]"
    );

    // Remove email addresses
    sanitized = sanitized.replace(
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
      "[EMAIL]"
    );

    // Remove phone numbers
    sanitized = sanitized.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, "[PHONE]");

    // Truncate very long messages
    if (sanitized.length > 200) {
      sanitized = sanitized.substring(0, 200) + "...";
    }

    // If the message is too generic or empty, provide a default
    if (
      !sanitized ||
      sanitized.trim().length === 0 ||
      sanitized === "[REDACTED]"
    ) {
      return "An error occurred. Please try again.";
    }

    return sanitized;
  }

  /**
   * Log error securely for server-side debugging
   */
  private logError(error: unknown, context: string): void {
    try {
      const errorDetails = {
        context,
        timestamp: new Date().toISOString(),
        errorType: error instanceof Error ? error.name : typeof error,
        errorCode: apiErrorCodeFromUnknown(error),
        // Never retain query strings: they can include searches, tokens, and
        // other personal context. The path is enough to group failures.
        path: typeof window !== "undefined" ? window.location.pathname : "Server",
      };

      // Log with appropriate level
      if (error instanceof Error) {
        log("error", `[${context}] Error occurred:`, errorDetails);
      } else {
        log("warn", `[${context}] Unexpected error:`, errorDetails);
      }
    } catch {
      // Logging must never surface the original error payload as a fallback.
    }
  }

  /**
   * Create a sanitized error response
   */
  createSanitizedError(
    error: unknown,
    context: string = "Unknown",
    errorContext?: ErrorContext
  ): SanitizedError {
    const sanitizedMessage = this.sanitizeErrorMessage(error, context);

    return {
      message: sanitizedMessage,
      code: this.getErrorCode(error),
      context,
      timestamp: Date.now(),
      requestId: errorContext?.requestId,
    };
  }

  /**
   * Get error code for error classification
   */
  private getErrorCode(error: unknown): string {
    if (error instanceof Error) {
      if (error.name === "TypeError") return "TYPE_ERROR";
      if (error.name === "ReferenceError") return "REFERENCE_ERROR";
      if (error.name === "SyntaxError") return "SYNTAX_ERROR";
      if (error.name === "NetworkError") return "NETWORK_ERROR";
      if (error.name === "TimeoutError") return "TIMEOUT_ERROR";
      return "GENERIC_ERROR";
    }

    if (typeof error === "string") {
      return "STRING_ERROR";
    }

    return "UNKNOWN_ERROR";
  }

  /**
   * Handle API errors specifically
   * Industry Best Practice: Don't sanitize user-facing error messages (401, 400, 422)
   * Only sanitize internal errors (500+) and stack traces
   */
  handleApiError(error: unknown, context: string = "API"): SanitizedError {
    let errorMessage = "An error occurred while processing your request";
    let errorCode = "API_ERROR";

    if (error && typeof error === "object" && "response" in error) {
      const apiError = error as {
        response?: {
          status?: number;
          data?: {
            detail?: string | { validation_errors?: any[] };
            message?: string;
            validation_errors?: any[];
            error?: {
              code?: string;
              error_code?: string;
              message?: string;
              details?: any;
            };
          };
          statusText?: string;
        };
        message?: string;
      };

      const status = apiError.response?.status;
      const data = apiError.response?.data;

      // Extract error message from different response formats
      const extractMessage = (): string => {
        if (
          data?.error &&
          typeof data.error === "object" &&
          data.error.message
        ) {
          return data.error.message;
        }
        if (data?.detail) {
          if (typeof data.detail === "string") {
            return data.detail;
          }
          // FastAPI: HTTPException(detail=create_registration_error(...).model_dump())
          // → { detail: { success, error: { error_code, message, details } } }
          if (typeof data.detail === "object" && data.detail !== null) {
            const nested = data.detail as { error?: { message?: string } };
            if (typeof nested.error?.message === "string") {
              return nested.error.message;
            }
          }
        }
        if (data?.message) {
          return data.message;
        }
        return "";
      };

      const rawMessage = extractMessage();

      // Handle validation errors (422) - don't sanitize, these are user-facing
      if (status === 422 && data?.detail) {
        if (typeof data.detail === "object" && data.detail.validation_errors) {
          const validationErrors = data.detail.validation_errors;
          const errorMessages = validationErrors
            .map((err: any) => err.message || err.detail || "Invalid input")
            .join(", ");
          errorMessage = errorMessages;
          errorCode = "VALIDATION_ERROR";
        } else if (typeof data.detail === "string") {
          errorMessage = data.detail; // Don't sanitize - user-facing
          errorCode = "VALIDATION_ERROR";
        } else if (Array.isArray(data.detail)) {
          // FastAPI / Pydantic: detail is [{ loc, msg, type }, ...]
          type ValidationItem = { loc?: unknown[]; msg?: string };
          const items = data.detail as ValidationItem[];
          const locHas = (field: string) => (item: ValidationItem) =>
            Array.isArray(item?.loc) &&
            item.loc!.some(
              (x) => String(x).toLowerCase() === field.toLowerCase()
            );
          const isMissing = (item: ValidationItem) =>
            /required|missing|field required/i.test(String(item?.msg ?? ""));
          const passwordMissing = items.some(
            (item) => locHas("password")(item) && isMissing(item)
          );
          const usernameMissing = items.some(
            (item) => locHas("username")(item) && isMissing(item)
          );
          if (passwordMissing && usernameMissing) {
            errorMessage =
              "Please enter your email address and password, then try again.";
          } else if (passwordMissing) {
            errorMessage = "Please enter your password, then try again.";
          } else if (usernameMissing) {
            errorMessage = "Please enter your email address, then try again.";
          } else {
            const msgs = items
              .map((item) =>
                typeof item?.msg === "string" ? item.msg : null
              )
              .filter(Boolean) as string[];
            errorMessage =
              msgs.length > 0
                ? msgs.join(" ")
                : "Some information is missing or invalid. Please check the form and try again.";
          }
          errorCode = "VALIDATION_ERROR";
        }
      }
      // Handle structured error responses (400) - don't sanitize
      else if (status === 400 && data?.error) {
        if (typeof data.error === "object" && data.error.message) {
          errorMessage = this.looksLikeInternalDbError(data.error.message)
            ? this.userFacingDbFallback()
            : data.error.message;
          errorCode = apiErrorCodeFromUnknown({ response: { data } }) || "VALIDATION_ERROR";
        }
      }
      // Handle authentication errors (401) - don't sanitize, preserve user-friendly messages
      else if (status === 401) {
        if (rawMessage) {
          // Check if it's a user-friendly authentication error message
          const messageLower = rawMessage.toLowerCase();
          if (
            messageLower.includes("incorrect") ||
            messageLower.includes("password") ||
            messageLower.includes("username") ||
            messageLower.includes("invalid credentials") ||
            messageLower.includes("authentication failed")
          ) {
            errorMessage = rawMessage; // Don't sanitize - user-facing auth error
          } else {
            errorMessage = "Authentication required. Please log in.";
          }
        } else {
          errorMessage = "Authentication required. Please log in.";
        }
        errorCode = "AUTH_REQUIRED";
      } else if (status === 403) {
        const claimPendingMessage = messageForClaimPendingEditRestriction(error);
        if (claimPendingMessage) {
          errorMessage = claimPendingMessage;
          errorCode = "CLAIM_PENDING_PROFILE_EDIT_RESTRICTED";
        } else {
          // Use server message when present (e.g. structured login block for unverified email)
          errorMessage =
            rawMessage || "You do not have permission to perform this action.";
          const structuredCode =
            data?.error &&
            typeof data.error === "object" &&
            typeof (data.error as { code?: string }).code === "string"
              ? (data.error as { code: string }).code
              : undefined;
          errorCode = structuredCode || "PERMISSION_DENIED";
        }
      } else if (status === 404) {
        errorMessage = "The requested resource was not found.";
        errorCode = "NOT_FOUND";
      } else if (status === 400) {
        // Handle 400 errors - don't sanitize, these are user-facing
        if (data?.detail) {
          if (
            typeof data.detail === "object" &&
            data.detail.validation_errors
          ) {
            const validationErrors = data.detail.validation_errors;
            const errorMessages = validationErrors
              .map((err: any) => err.message || err.detail || "Invalid input")
              .join(", ");
            errorMessage = errorMessages;
            errorCode = "VALIDATION_ERROR";
          } else if (typeof data.detail === "string") {
            errorMessage = this.looksLikeInternalDbError(data.detail)
              ? this.userFacingDbFallback()
              : data.detail;
            errorCode = "VALIDATION_ERROR";
          } else if (typeof data.detail === "object" && data.detail !== null) {
            const regDetail = data.detail as {
              error?: { message?: string; code?: string; error_code?: string };
            };
            if (typeof regDetail.error?.message === "string") {
              errorMessage = this.looksLikeInternalDbError(
                regDetail.error.message
              )
                ? this.userFacingDbFallback()
                : regDetail.error.message;
              errorCode =
                apiErrorCodeFromUnknown({ response: { data } }) || "VALIDATION_ERROR";
            }
          }
        } else if (rawMessage) {
          errorMessage = this.looksLikeInternalDbError(rawMessage)
            ? this.userFacingDbFallback()
            : rawMessage;
          errorCode = "VALIDATION_ERROR";
        }
      } else if (status === 429) {
        errorMessage = "Too many requests. Please try again later.";
        errorCode = "RATE_LIMITED";
      } else if (status && status >= 500) {
        // Only sanitize server errors (500+) as they may contain internal information
        errorMessage = "Server error. Please try again later.";
        errorCode = "SERVER_ERROR";
      } else if (rawMessage) {
        // For other status codes, check if it's a user-facing message
        // If it contains common user-facing patterns, don't sanitize
        const messageLower = rawMessage.toLowerCase();
        const isUserFacing =
          messageLower.includes("incorrect") ||
          messageLower.includes("invalid") ||
          messageLower.includes("required") ||
          messageLower.includes("not found") ||
          messageLower.includes("permission") ||
          messageLower.includes("validation");

        if (isUserFacing) {
          errorMessage = rawMessage; // Don't sanitize - user-facing
        } else {
          errorMessage = this.sanitizeString(rawMessage); // Sanitize - may contain internal info
        }
      } else if (apiError.message) {
        errorMessage = this.sanitizeString(apiError.message);
      }
    } else if (error instanceof Error) {
      // Only sanitize Error messages if they might contain sensitive info
      // Check if it looks like a user-friendly message
      const messageLower = error.message.toLowerCase();
      const isUserFacing =
        messageLower.includes("incorrect") ||
        messageLower.includes("invalid") ||
        messageLower.includes("required") ||
        messageLower.includes("not found");

      if (isUserFacing) {
        errorMessage = error.message; // Don't sanitize - user-facing
      } else {
        errorMessage = this.sanitizeString(error.message); // Sanitize - may contain internal info
      }
    }

    // Log expected client-side API outcomes (validation/auth/duplicate/conflict)
    // at debug level to reduce noisy frontend ERROR lines in normal flows.
    const apiStatus =
      error && typeof error === "object" && "response" in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;
    const expectedClientErrorStatuses = [400, 401, 403, 404, 409, 422, 429];
    const isExpectedClientError =
      typeof apiStatus === "number" &&
      expectedClientErrorStatuses.includes(apiStatus);

    if (isExpectedClientError) {
      log("debug", `[${context}] API client error (expected):`, {
        status: apiStatus,
        code: errorCode,
        message: errorMessage,
      });
    } else if (
      errorCode === "AUTH_REQUIRED" &&
      errorMessage.toLowerCase().includes("incorrect")
    ) {
      log("debug", `[${context}] Auth failure (expected):`, {
        message: errorMessage,
      });
    } else {
      this.logError(error, context);
    }

    return {
      message: errorMessage,
      code: errorCode,
      context,
      timestamp: Date.now(),
    };
  }

  /**
   * Check if an error contains sensitive information
   */
  containsSensitiveInfo(error: unknown): boolean {
    if (error instanceof Error) {
      return this.sensitivePatterns.some(
        (pattern) =>
          pattern.test(error.message) || pattern.test(error.stack || "")
      );
    }

    if (typeof error === "string") {
      return this.sensitivePatterns.some((pattern) => pattern.test(error));
    }

    return false;
  }

  /**
   * Get security status of error handling
   */
  getSecurityStatus(): {
    isSecure: boolean;
    patternsLoaded: number;
    sensitivePatterns: number;
    errorPatterns: number;
  } {
    return {
      isSecure: true,
      patternsLoaded: this.sensitivePatterns.length + this.errorPatterns.length,
      sensitivePatterns: this.sensitivePatterns.length,
      errorPatterns: this.errorPatterns.length,
    };
  }
}

// Export singleton instance
export const secureErrorHandler = SecureErrorHandler.getInstance();

// Export utility functions for backward compatibility
export const sanitizeError = (error: unknown, context?: string): string =>
  secureErrorHandler.sanitizeErrorMessage(error, context);

export const handleApiError = (
  error: unknown,
  context?: string
): SanitizedError => secureErrorHandler.handleApiError(error, context);

export const createSanitizedError = (
  error: unknown,
  context?: string,
  errorContext?: ErrorContext
): SanitizedError =>
  secureErrorHandler.createSanitizedError(error, context, errorContext);
